import { HttpBackend, HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, defer, finalize, firstValueFrom, from, of, shareReplay, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { cleanLocalStorage } from '../../shared/utils/generic';
import { LoggerService } from './utils/logger.service';
import { RequestsService } from './utils/requests.service';

export interface RefreshTokenResponse { access_token: string; expires_in?: number; }

export const AUTH_REFRESH_LOCKS = new InjectionToken<LockManager | undefined>('Auth refresh locks', {
  providedIn: 'root', factory: () => typeof navigator === 'undefined' ? undefined : navigator.locks
});

/** Identifies a login without treating an access-token renewal as a new session. */
export function authenticationSessionKey(): string {
  const context = localStorage.getItem('WITA_LOG_CONTEXT');
  if (context) return context;
  try { return String(JSON.parse(localStorage.getItem('USER') ?? 'null')?.id ?? ''); }
  catch { return ''; }
}

@Injectable({ providedIn: 'root' })
export class SessionRefreshService {
  private readonly http = new HttpClient(inject(HttpBackend));
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);
  private readonly requests = inject(RequestsService);
  private readonly locks = inject(AUTH_REFRESH_LOCKS);
  private inFlight?: { session: string; request: Observable<HttpResponse<RefreshTokenResponse>> };

  refresh(): Observable<HttpResponse<RefreshTokenResponse>> {
    return defer(() => {
      const session = authenticationSessionKey();
      if (this.inFlight?.session === session) return this.inFlight.request;
      const originalToken = localStorage.getItem('ACCESS_TOKEN');
      const unchanged = () => authenticationSessionKey() === session && localStorage.getItem('ACCESS_TOKEN') === originalToken;
      const send = () => defer(() => {
        if (authenticationSessionKey() !== session) return throwError(() => new Error('AUTH_SESSION_CHANGED'));
        const currentToken = localStorage.getItem('ACCESS_TOKEN');
        // Another tab can complete the renewal while this tab waits for the cookie lock.
        if (currentToken && currentToken !== originalToken) {
          return of(new HttpResponse({ body: { access_token: currentToken }, status: 200 }));
        }
        if (currentToken !== originalToken) return throwError(() => new Error('AUTH_SESSION_CHANGED'));
        return this.http.post<RefreshTokenResponse>(`${environment.apiEndpoint}/auth/refreshToken`, {}, {
          observe: 'response', withCredentials: true,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
        }).pipe(tap(response => {
          if (!unchanged()) throw new Error('AUTH_SESSION_CHANGED');
          if (!response.body?.access_token) throw new Error('AUTH_REFRESH_TOKEN_MISSING');
          localStorage.setItem('ACCESS_TOKEN', response.body.access_token);
        }));
      });
      const entry = { session, request: undefined as unknown as Observable<HttpResponse<RefreshTokenResponse>> };
      entry.request = defer(() => this.locks
        ? from((async () => await this.locks!.request('wita-auth-refresh', () => firstValueFrom(send())))())
        : send()
      ).pipe(
        catchError(error => {
          if (unchanged() && error instanceof HttpErrorResponse && [401, 403].includes(error.status)) {
            this.logger.error('AUTH_REFRESH_REJECTED');
            cleanLocalStorage();
            this.requests.saveCurrentUser(null);
            void this.router.navigate(['/auth/login']);
          }
          return throwError(() => error);
        }),
        finalize(() => { if (this.inFlight === entry) this.inFlight = undefined; }),
        // Keep the cookie rotation running if an individual API caller unsubscribes.
        shareReplay({ bufferSize: 1, refCount: false })
      );
      this.inFlight = entry;
      return entry.request;
    });
  }
}
